import { useState } from 'react';
import { useCanAccess, useNotify, useRecordContext, useRefresh } from 'ra-core';
import {
  masquerUnCours,
  terminerUnCours,
  observerUnCoursEnDirect,
} from '@groupe-trousseau/shared/api-client';
import { Button, buttonVariants } from '@/components/ui/button';
import { Confirm } from '@/components/admin/confirm';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  adresseDeLApplication,
  deballer,
  lireLErreur,
} from '../../fournisseurs/client';
import { ModificationDunCours } from './ModificationDunCours';
import { ReleveDunCours } from './ReleveDunCours';
import { RevocationDunAcces } from './RevocationDunAcces';

type Cours = { id: string; titre: string; enCours: boolean; masque: boolean };

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LES GESTES SUR UN COURS — F-63, ET CE SONT DES GESTES SUR LA LIGNE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **Cinq gestes vivent ici, et le dernier est arrivé le 20260830** : modifier le titre et la
 * description (`ModificationDunCours`, D-120), couper, masquer, **annuler l'accès d'une
 * personne** (`RevocationDunAcces`, `CDC-1` F-12, D-130 arbitrage 3), **lire le relevé des
 * entrées et des sorties** (`ReleveDunCours`, D-084) — plus l'observation.
 * F-62 : « **deux des dix écrans rendus ne sont pas des écrans** ».
 *
 * **LE CINQUIÈME EST CELUI QUI EXPLIQUE LA DURÉE AFFICHÉE SUR CETTE MÊME LIGNE**, et c'est
 * pourquoi il vit ici et non ailleurs : D-084 le motive par « une personne qui administre voit
 * un nombre de minutes et **n'a aucun moyen de savoir d'où il vient** ». Le mettre sur un
 * écran séparé aurait remis la question à un clic de distance du nombre qu'elle pose.
 *
 * **Le quatrième vient d'un autre document que les trois autres**, et c'est le seul de cette
 * colonne dans ce cas : `CDC-5` §6.14 ne porte aucune ligne pour lui, la règle vivant en
 * `CDC-1` F-12 avec ses quatre identités. Le motif de sa place ici est celui de F-63 malgré
 * tout : il désigne **une personne sur un cours**, et le cours est la ligne.
 *
 * F-63 : « **masquer un cours est un geste sur la ligne**, et le compte qui voit tout est SEUL
 * à l'avoir. **Il passe par cette surface, et par elle seule** (Q566) : `CDC-2` en portait un
 * second chemin, **il est retiré**. » Même chose de l'observation : « **observer un cours en
 * direct passe aussi par cette surface, et par elle seule** (Q566), **avec sa borne déclarée
 * sur la route** (Q567). »
 *
 * **Couper un cours en cours** appartient aux deux, avec leur périmètre : « la personne qui
 * tient l'espace, **sur les cours portant sa structure seulement** ; le compte qui voit tout,
 * sur tous. » Et le geste vit sur `POST /api/v1/cours/:id/fin`, route de `CDC-2` — le second
 * amendement de §7 a retiré le doublon de titre et description pour la même raison ; celui-ci
 * n'a jamais eu de doublon.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **L'OBSERVATION : CE QU'ELLE MONTRE, ET CE QU'ELLE NE MONTRERA JAMAIS.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-64 : « **ce qu'elle montre, et rien de plus** : le tableau qui se remplit, les curseurs
 * nommés, les écrans partagés. **JAMAIS L'IMAGE NI LE SON**, refus explicite aux deux rôles. »
 * §6.14 le porte en champ interdit, pour les deux : « **l'IMAGE et le SON, toujours** ».
 *
 * « **Le passage d'une personne qui administre une structure est signalé par un bandeau ; celui
 * du compte qui voit tout ne l'est pas**, son passage restant constatable **par sa trace**. »
 * C'est le serveur qui le décide et le rend, `signalee` — un test de rôle ici serait la
 * deuxième copie de la règle.
 *
 * « **Une structure n'observe qu'un cours dont un de ses élèves est présent.** Cette borne est
 * déclarée **sur la route qui porte le geste** (Q567) » — et §7 en donne la raison générale :
 * « **une borne d'autorisation qui dépend du chemin emprunté est une faille par
 * construction**. » Le refus arrive nommé, `AUCUN_ELEVE_DE_CETTE_STRUCTURE`.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE BOUTON OUVRE L'OBSERVATION ET RENVOIE VERS ELLE. IL NE LA MONTRE PAS ICI, ET
 * C'EST UNE DÉCISION D'ARCHITECTURE — 20260829.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * **La surface d'observation vit dans `web/`, à une adresse en lecture stricte, et cette
 * surface-ci y renvoie par URL.** Le motif est un port : **le moteur de tableau vit dans
 * `web/src/features/cours/moteur.tsx`, seul fichier du dépôt autorisé à le connaître** — port
 * étroit de D-012, gardé par quatre sondes. Le faire importer par `admin/` casserait ce port ;
 * le dupliquer créerait une seconde vérité sur le rendu d'une scène. **Un renvoi par URL n'est
 * pas un import** : aucune barrière de couche n'a à le refuser, et rien de `web/` n'entre ici.
 *
 * **L'ORDRE DES DEUX GESTES N'EST PAS INTERCHANGEABLE.** L'appel part d'abord, le renvoi
 * ensuite — et jamais l'inverse. Trois raisons, toutes au corpus :
 *
 * 1. **La borne de Q567 est sur cette route, et sur elle seule** (Q566) : « une structure
 *    n'observe qu'un cours dont **un de ses élèves** est présent ». Renvoyer d'abord ferait
 *    porter le refus à une page de `web/`, c'est-à-dire **une autorisation qui dépend du chemin
 *    emprunté**, « une faille par construction » selon §7.
 * 2. **C'est l'appel qui ouvre la LIGNE d'observation**, et le canal s'y adosse : `admettre()`
 *    a une **seconde porte** pour l'observateur, qui ne rejoue aucun droit — elle vérifie
 *    « **que le geste autorisé a eu lieu** : une ligne d'observation ouverte ». Sans cet appel,
 *    la page de `web/` serait refusée au salon, et le refus dirait « pas participant ».
 * 3. **C'est lui qui déclenche le bandeau** chez les présents quand `signalee` est vrai.
 *
 * **Ce que D-073 avait remonté est donc levé côté serveur** — « le canal n'admet pas encore
 * d'observateur, `admettre()` exigeant d'être participant » : la seconde porte d'admission
 * existe désormais, et elle porte en propre la phrase de F-20 sur ce qu'un observateur reçoit.
 *
 * **CE QUE L'ÉCRAN N'AFFIRME PAS POUR AUTANT.** Le lien ouvre une adresse ; il ne prouve pas
 * qu'une scène s'y affiche. L'encart dit ce que l'observation montre **et ce qu'elle ne montre
 * jamais**, ce qui est une règle du produit, jamais un constat de session.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **MASQUER N'EST PAS SUPPRIMER, ET LA DIFFÉRENCE PORTE SUR UN CHIFFRE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-62 : « **un cours masqué compte quand même dans la somme de sa structure**, alors qu'il a
 * disparu de la liste des participants. » Et F-67 : « **un cours masqué compte quand même** dans
 * ce chiffre : **le masquage vise la liste d'une personne, jamais la mémoire du parc.** » La
 * confirmation le dit, parce que « masquer » se lit spontanément comme « retirer ».
 *
 * **Aucun des deux rôles n'écrit sur une scène ni ne l'efface avant son terme** (F-63) : il n'y
 * a donc aucun geste d'écriture ici, et la matrice porte `scenes.edit` et `scenes.delete` à
 * `PERSONNE`.
 */
export function GestesSurUnCours() {
  const cours = useRecordContext<Cours>();
  const notifier = useNotify();
  const rafraichir = useRefresh();

  const { canAccess: peutMasquer } = useCanAccess({ resource: 'cours', action: 'masquer' });
  const { canAccess: peutCouper } = useCanAccess({ resource: 'cours', action: 'couper' });
  const { canAccess: peutObserver } = useCanAccess({
    resource: 'observations',
    action: 'show',
  });

  const [aConfirmer, setAConfirmer] = useState<'masquage' | 'coupure' | null>(null);
  /**
   * **Ce que l'écran retient de l'observation ouverte, et pas un champ de plus.**
   *
   * Le contrat en rend six. `observationId` et `tableauCourantId` **ne servent pas ici** : la
   * ligne de trace se constate hors du produit — « aucun écran de consultation de trace n'existe
   * en V1 » —, et la scène à charger est apprise **par le salon**, la surface d'observation étant
   * dans `web/`. `titre` est déjà sur la ligne du cours. Les recopier ferait afficher des
   * identifiants que personne ne peut employer.
   */
  const [observation, setObservation] = useState<{
    coursId: string;
    signalee: boolean;
    nombreDeTableaux: number;
  } | null>(null);

  if (cours === undefined) return null;

  const agir = async (action: () => Promise<unknown>, message: string) => {
    try {
      await action();
      notifier(message, { type: 'info' });
      rafraichir();
    } catch (erreur) {
      notifier(lireLErreur(erreur).message, { type: 'error' });
    } finally {
      setAConfirmer(null);
    }
  };

  const observer = async () => {
    try {
      const ouverte = await deballer(observerUnCoursEnDirect({ path: { id: cours.id } }));
      setObservation({
        // **L'identifiant vient de la RÉPONSE, jamais de la ligne affichée.** Les deux valent
        // la même chose aujourd'hui ; la réponse est celle du geste qui vient d'être autorisé,
        // et c'est elle qui désigne le cours dont l'observation est ouverte.
        coursId: ouverte.coursId,
        signalee: ouverte.signalee,
        nombreDeTableaux: ouverte.nombreDeTableaux,
      });
    } catch (erreur) {
      const lue = lireLErreur(erreur);
      notifier(
        lue.code === 'AUCUN_ELEVE_DE_CETTE_STRUCTURE'
          ? 'Aucun élève de votre structure n’est présent sur ce cours : vous n’observez que ' +
              'les cours où l’un des vôtres se trouve.'
          : lue.message,
        { type: 'error' },
      );
    }
  };

  /**
   * **L'adresse de la surface d'observation, chez `web/`.** Elle se calcule à partir de la
   * réponse du geste, jamais avant : tant que l'appel n'a pas abouti, il n'y a rien à ouvrir.
   */
  const application = adresseDeLApplication();
  const adresseDeLObservation =
    observation === null || application === null
      ? null
      : `${application}/observation/${observation.coursId}`;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-1">
        {/* **MODIFIER LE TITRE ET LA DESCRIPTION — F-63, câblé le 20260828, D-120.**
            C'était la dernière des trois orphelines de D-108 : la redirection de D-071
            avait vérifié que `PATCH /api/v1/cours/:id` existe et accorde le rôle, et
            **aucun écran ne l'appelait**. Le geste n'est pas borné aux cours en cours :
            F-63 ne le conditionne pas, et le titre d'un cours passé se corrige. */}
        <ModificationDunCours />
        {peutObserver && cours.enCours && (
          <Button variant="outline" size="sm" onClick={() => void observer()}>
            Observer
          </Button>
        )}
        {peutCouper && cours.enCours && (
          <Button variant="outline" size="sm" onClick={() => setAConfirmer('coupure')}>
            Couper
          </Button>
        )}
        {peutMasquer && !cours.masque && (
          <Button variant="outline" size="sm" onClick={() => setAConfirmer('masquage')}>
            Masquer
          </Button>
        )}
        {/* **ANNULER L'ACCÈS D'UNE PERSONNE — `CDC-1` F-12, câblé le 20260829, D-130
            arbitrage 3.** Le geste ne consulte aucune cellule de §6.14 : la matrice n'en
            porte pas pour lui, et `CDC-1` §7 donne la route aux deux rôles de cette
            surface sans condition de rôle. Il se propose sur un cours **en cours** seul,
            et c'est le composant qui le décide — à la fin d'un cours, tous les liens
            encore émis deviennent caducs, donc le geste ne pourrait plus rien faire. */}
        <RevocationDunAcces />
        {/* **LE RELEVÉ DES ENTRÉES ET DES SORTIES — D-084, câblé le 20260830.** La route
            était déclarée due depuis le 20260824 et n'existait pas ; `matrice.ts` portait
            sa ligne à `PERSONNE` **avec la mention que c'était le seul `PERSONNE`
            provisoire de la table**. Elle porte `LES_DEUX` depuis que la route existe.
            **Il ne se borne pas aux cours en cours** : c'est le relevé d'un cours terminé
            qu'on relit pour facturer. */}
        <ReleveDunCours />
      </div>

      {observation !== null && (
        <Alert>
          <AlertTitle>
            Observation ouverte — {observation.nombreDeTableaux} tableau(x)
            {observation.signalee
              ? ', signalée aux présents par un bandeau.'
              : ', non signalée aux présents.'}
          </AlertTitle>
          <AlertDescription>
            <span className="block">
              Ce que l’observation montre : le tableau qui se remplit, les
              curseurs nommés, les écrans partagés.{' '}
              <strong>Jamais l’image ni le son</strong> — refus explicite aux
              deux rôles, et le contrat ne les porte pas. Vous ne pouvez rien y
              écrire, ni effacer une scène.
            </span>
            <span className="mt-2 block">
              {observation.signalee
                ? 'Votre passage est signalé aux présents par un bandeau, et il laisse une ligne.'
                : 'Votre passage n’est pas signalé aux présents, et il laisse une ligne — c’est le seul moyen de le constater après coup.'}
            </span>
            {adresseDeLObservation === null ? (
              /**
               * **La variable manquante se NOMME, et l'écran ne renvoie nulle part.** Même
               * posture que `VITE_SITE_URL` : un repli relatif ouvrirait un onglet sur cette
               * surface-ci, qui ne sert pas cette adresse — donc une page qui a l'air de
               * marcher. L'observation, elle, EST ouverte : l'appel a abouti, et la ligne est
               * écrite. C'est le renvoi qui manque, pas le geste.
               */
              <span className="mt-3 block">
                L’observation est ouverte, mais cette surface ne sait pas où se
                trouve l’application de séance : la variable de construction{' '}
                <code className="font-mono text-xs">VITE_APP_URL</code> n’a pas
                été fournie à cette image. C’est une variable de{' '}
                <strong>build</strong> — la poser en variable d’exécution ne
                ferait rien.
              </span>
            ) : (
              <a
                className={
                  buttonVariants({ variant: 'default', size: 'sm' }) + ' mt-3'
                }
                href={adresseDeLObservation}
                target="_blank"
                rel="noopener"
              >
                Ouvrir l’observation
              </a>
            )}
          </AlertDescription>
        </Alert>
      )}

      <Confirm
        isOpen={aConfirmer === 'coupure'}
        title="Couper ce cours en train de se dérouler ?"
        content={
          'Le cours se termine immédiatement pour tous les présents. La durée déjà écoulée reste ' +
          'comptée dans la somme des minutes de la structure.'
        }
        onConfirm={() =>
          void agir(
            () => deballer(terminerUnCours({ path: { id: cours.id } })),
            'Cours coupé.',
          )
        }
        onClose={() => setAConfirmer(null)}
      />

      <Confirm
        isOpen={aConfirmer === 'masquage'}
        title="Masquer ce cours ?"
        content={
          'Le masquage vise la liste des participants, jamais la mémoire du parc : le cours ' +
          'continue de compter dans la somme des minutes de sa structure et dans le nombre de ' +
          'cours tenus. Ce n’est pas une suppression — il n’en existe aucune.'
        }
        onConfirm={() =>
          void agir(
            () => deballer(masquerUnCours({ path: { id: cours.id } })),
            'Cours masqué. Il compte toujours dans la somme des minutes.',
          )
        }
        onClose={() => setAConfirmer(null)}
      />
    </div>
  );
}
